import PropTypes from 'prop-types';
import { IconButton, Stack } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { AddCircle, RemoveCircle } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useFeatures } from '@paralleldrive/react-feature-toggles';
import { v4 as uuidv4 } from 'uuid';
import CustomSelect from '../../../CustomSelect';
import CustomTooltip from '../../../CustomTooltip';
import { useGetAll } from '../../../../../../hooks/useGetAll';
import { useParts } from '../../../../../../hooks/useParts';

const clearOldSensors = (values, prev) => {
  const selectedSensors = new Set(values.items.map(i => i.sensor));
  Object.keys(prev).forEach(s => {
    if (!selectedSensors.has(s)) {
      delete prev[s];
    }
  });
};

export const componentTypes = {
  SENSOR: 'SEN',
  PART: 'PRT',
  ACTUATOR: 'ACT',
  NOTHING: 'NOT',
};

const SensorSelector = ({
  assets,
  values,
  handleChange,
  handleBlur,
  setFieldValue,
  errors,
  touched,
  index,
  arrayHelpers,
  isList,
  measuringModeFilter,
  hasExclusive,
  setHasExclusive,
  disableActuators,
  disableParts,
  validateForm,
}) => {
  const { t } = useTranslation();
  const features = useFeatures();
  const actuatorsEnabled = useMemo(
    () => features.includes('zigbee2mqtt'),
    [features],
  );
  const selectedAsset = useMemo(
    () => (isList ? values.items[index].asset : values.asset),
    [index, isList, values.asset, values.items],
  );
  const selectedSensor = useMemo(
    () => (isList ? values.items[index].sensor : values.sensor),
    [index, isList, values.items, values.sensor],
  );

  const { data: partsData, isFetching: isFetchingPartsData } = useParts(
    {
      filter: { asset: selectedAsset },
      pageSize: 300,
    },
    !!selectedAsset,
  );
  const { data: { sensor: sensors } = {}, isFetching: isFetchingSensors } =
    useGetAll(
      'sensor',
      {
        filter: {
          asset: selectedAsset,
        },
      },
      !!selectedAsset,
    );
  const { data: { part: parts } = {}, isFetching: isFetchingParts } = useGetAll(
    'part',
    {
      filter: {
        asset: selectedAsset,
      },
    },
    !disableParts && !!selectedAsset,
  );
  const { data: { device: actuators } = {}, isFetching: isFetchingActuators } =
    useGetAll(
      ['device'],
      {
        filter: {
          asset: selectedAsset,
          actuator: true,
        },
      },
      actuatorsEnabled && !disableActuators && !!selectedAsset,
    );

  useEffect(() => {
    if (!setHasExclusive) return;
    const sensor = sensors?.find(s => s.id === selectedSensor);
    const actuator = actuators?.find(a => a.id === selectedSensor);
    const part = partsData?.data?.find(a => a.id === selectedSensor);
    if (sensor || actuator || part) {
      setHasExclusive(prev => {
        clearOldSensors(values, prev);
        let val = componentTypes.NOTHING;
        if (actuator) val = componentTypes.ACTUATOR;
        else if (part) val = componentTypes.PART;
        else if (
          sensor.measuring_mode === 'streaming' ||
          sensor.measuring_mode === 'positioning'
        )
          val = componentTypes.SENSOR;
        return {
          ...prev,
          [selectedSensor]: val,
        };
      });
    } else {
      setHasExclusive(prev => {
        clearOldSensors(values, prev);
        return { ...prev };
      });
    }
    validateForm();
  }, [
    actuators,
    index,
    isList,
    partsData,
    selectedSensor,
    sensors,
    setHasExclusive,
    validateForm,
    values,
  ]);

  const componentOptions = useMemo(() => {
    const makeOptionList = (items, categoryKey, titleKey, isDisabledFn) => {
      if (!items?.length) return null;
      return {
        category: {
          value: categoryKey,
          label: t(titleKey),
        },
        list: items.map(item => ({
          ...item,
          disabled: isDisabledFn(item),
        })),
      };
    };

    const itemsLength = values.items.length;

    const disableForSensor = s =>
      selectedSensor !== s.value &&
      ((hasExclusive && itemsLength > 1) ||
        measuringModeFilter?.includes(s.measuring_mode) ||
        values.items.some(i => i.sensor === s.value));

    const disableForActuatorOrPart = a =>
      selectedSensor !== a.value &&
      ((hasExclusive && itemsLength > 1) || !!measuringModeFilter);

    return [
      makeOptionList(
        sensors,
        'sensors',
        'management_menu.sensor_management.title',
        disableForSensor,
      ),
      !disableActuators &&
        makeOptionList(
          actuators,
          'actuators',
          'management_menu.actuator_management.title',
          disableForActuatorOrPart,
        ),
      !disableParts &&
        makeOptionList(
          parts,
          'parts',
          'management_menu.part_management.title',
          disableForActuatorOrPart,
        ),
    ].filter(Boolean);
  }, [
    sensors,
    actuators,
    parts,
    disableActuators,
    disableParts,
    hasExclusive,
    measuringModeFilter,
    selectedSensor,
    t,
    values.items,
  ]);

  const isFetching =
    isFetchingSensors ||
    isFetchingActuators ||
    isFetchingParts ||
    isFetchingPartsData;

  return (
    <Stack direction="row" alignItems="flex-start" spacing={2}>
      <Stack
        width={`calc(100% - ${isList ? 100 : 0}px)`}
        direction="row"
        alignItems="flex-start"
        spacing={2}
      >
        <CustomSelect
          id={isList ? `items.${index}.asset` : 'asset'}
          label={t('atoms.widget.selector.asset')}
          options={assets}
          value={selectedAsset}
          onChange={e => {
            handleChange(e);
            setFieldValue(isList ? `items.${index}.sensor` : 'sensor', '');
          }}
          onBlur={handleBlur}
          errors={errors}
          touched={touched}
          disabled={!assets || assets.length === 0}
          required
        />
        <CustomSelect
          id={isList ? `items.${index}.sensor` : 'sensor'}
          label={
            disableActuators
              ? t('atoms.widget.selector.sensor')
              : t('configuration_menu.rules_trigger.all_rules.component')
          }
          optionsGroups={componentOptions}
          value={selectedSensor}
          onChange={handleChange}
          onBlur={handleBlur}
          errors={errors}
          touched={touched}
          disabled={!componentOptions || componentOptions.length === 0}
          required
          isLoading={isFetching}
        />
      </Stack>
      {isList && values.items.length - 1 === index && (
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          paddingTop="50px"
        >
          <CustomTooltip title={t('atoms.widget.selector.add')}>
            <span>
              <IconButton
                color="primary"
                sx={{ padding: 0 }}
                disabled={values.items.length > 3 || hasExclusive || isFetching}
                onClick={() =>
                  arrayHelpers.push({ id: uuidv4(), asset: '', sensor: '' })
                }
              >
                <AddCircle fontSize="large" />
              </IconButton>
            </span>
          </CustomTooltip>
          <CustomTooltip title={t('atoms.widget.selector.remove')}>
            <span>
              <IconButton
                color="primary"
                sx={{ padding: 0 }}
                disabled={index === 0}
                onClick={() => arrayHelpers.remove(index)}
              >
                <RemoveCircle fontSize="large" />
              </IconButton>
            </span>
          </CustomTooltip>
        </Stack>
      )}
    </Stack>
  );
};

SensorSelector.propTypes = {
  arrayHelpers: PropTypes.shape({
    push: PropTypes.func,
    remove: PropTypes.func,
  }).isRequired,
  assets: PropTypes.array.isRequired,
  errors: PropTypes.any.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  validateForm: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  isList: PropTypes.bool.isRequired,
  touched: PropTypes.any.isRequired,
  values: PropTypes.shape({
    asset: PropTypes.string,
    items: PropTypes.array,
    sensor: PropTypes.string,
  }).isRequired,
  measuringModeFilter: PropTypes.array,
  hasExclusive: PropTypes.bool,
  setHasExclusive: PropTypes.func,
  disableActuators: PropTypes.bool,
  disableParts: PropTypes.bool,
};

export default SensorSelector;
